import { useEffect, useMemo, useState } from "react";

export const PWA_DISMISS_KEY = "rsi_pwa_dismissed";

function detectStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
}

function detectIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent || "");
}

export default function useAppInstall() {
  const [installEvent, setInstallEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone());
  const [isOnline, setIsOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(PWA_DISMISS_KEY) === "1";
  });

  useEffect(() => {
    const standaloneHandler = () => setIsStandalone(detectStandalone());
    const onlineHandler = () => setIsOnline(true);
    const offlineHandler = () => setIsOnline(false);
    const installHandler = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };

    window.addEventListener("beforeinstallprompt", installHandler);
    window.addEventListener("appinstalled", standaloneHandler);
    window.addEventListener("online", onlineHandler);
    window.addEventListener("offline", offlineHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", installHandler);
      window.removeEventListener("appinstalled", standaloneHandler);
      window.removeEventListener("online", onlineHandler);
      window.removeEventListener("offline", offlineHandler);
    };
  }, []);

  const canInstall = useMemo(
    () => Boolean(installEvent) && !isStandalone,
    [installEvent, isStandalone]
  );

  const install = async () => {
    if (!installEvent) return { outcome: "unavailable" };
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice?.outcome === "accepted") {
      setInstallEvent(null);
      setDismissed(true);
      localStorage.setItem(PWA_DISMISS_KEY, "1");
    }
    return choice;
  };

  const dismiss = () => {
    localStorage.setItem(PWA_DISMISS_KEY, "1");
    setDismissed(true);
  };

  return {
    installEvent,
    isStandalone,
    isOnline,
    isIOS: detectIOS(),
    canInstall,
    dismissed,
    setDismissed,
    install,
    dismiss,
  };
}
